import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./SignIn.module.sass";
import { use100vh } from "react-div-100vh";
import { Link, useNavigate } from "react-router-dom";
import TextInput from "../../components/TextInput";
import PhoneInput from "../../components/PhoneInput";
import Image from "../../components/Image";
import Dropdown from "../../components/Dropdown";
import { useAuth } from "../../contexts/AuthContext";
import { showSuccess, showError } from "../../services/toast.service";
import { pb } from "../../services/pocketbase";
import Switch from "../../components/Switch";
import ModalCondition from "../../components/ModalConditionGeneral";
import ModalUseCondition from "../../components/ModalPolitiqueConfidentialite";
import ModalReglement from "../../components/ModalReglement";
import Icon from "../../components/Icon";
import Card from "../../components/Card";
import { getCurrentCompanyInfo } from "../../services/account.service";
import { updateCompany, getOpcoName } from "../../services/company.service";

// Options pour les secteurs d'activité
const SECTOR_OPTIONS = [
  { value: 'Technologies & Digital', title: 'Technologies & Digital' },
  { value: 'Finance & Assurance', title: 'Finance & Assurance' },
  { value: 'Santé & Pharmaceutique', title: 'Santé & Pharmaceutique' },
  { value: 'Éducation & Formation', title: 'Éducation & Formation' },
  { value: 'Commerce & Distribution', title: 'Commerce & Distribution' },
  { value: 'Industrie & Manufacturing', title: 'Industrie & Manufacturing' },
  { value: 'Services aux entreprises', title: 'Services aux entreprises' },
  { value: 'Transport & Logistique', title: 'Transport & Logistique' },
  { value: 'Énergie & Environnement', title: 'Énergie & Environnement' },
  { value: 'BTP & Construction', title: 'BTP & Construction' },
  { value: 'Agroalimentaire', title: 'Agroalimentaire' },
  { value: 'Médias & Communication', title: 'Médias & Communication' },
  { value: 'Tourisme & Hôtellerie', title: 'Tourisme & Hôtellerie' },
  { value: 'Autre', title: 'Autre' }
];

// Options pour les types d'entreprise
const COMPANY_TYPE_OPTIONS = [
  { value: 'TPE', title: 'TPE' },
  { value: 'PME', title: 'PME' },
  { value: 'ETI', title: 'ETI' },
  { value: 'Grande entreprise', title: 'Grande entreprise' },
  { value: 'Start-up', title: 'Start-up' },
  { value: 'Association', title: 'Association' },
  { value: 'Organisme public', title: 'Organisme public' },
  { value: 'Autre', title: 'Autre' }
];

// Paramètres pour les conditions à accepter
const settings = [
  {
    id: 0,
    title: "Conditions générales",
  },
  {
    id: 1,
    title: "Conditions d'utilisation",
  },
  {
    id: 2,
    title: "Rêglement intérieur",
  },
];

const CompleteAccountCompany = () => {
  const heightWindow = use100vh();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // États pour gérer les données de l'entreprise
  const [company, setCompany] = useState({});
  const [initialCompany, setInitialCompany] = useState({});
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedCompanyType, setSelectedCompanyType] = useState('');
  const [selectedSector, setSelectedSector] = useState('');

  // États pour gérer l'affichage des modales
  const [visibleCondition, setVisibleCondition] = useState(false);
  const [visibleUseCondition, setVisibleUseCondition] = useState(false);
  const [visibleReglement, setVisibleReglement] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // États pour les conditions à accepter
  const [acceptConditions, setAcceptConditions] = useState(false);
  const [acceptUseConditions, setAcceptUseConditions] = useState(false);
  const [acceptRules, setAcceptRules] = useState(false);

  // Détecter si l'affichage est en mode mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Vérifier au chargement
    checkIfMobile();

    // Ajouter un listener pour détecter les changements de taille d'écran
    window.addEventListener('resize', checkIfMobile);

    // Nettoyer le listener lors du démontage du composant
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Charger les données de l'entreprise depuis PocketBase
  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        setLoading(true);
        // Vérifier si l'utilisateur est connecté
        if (!pb.authStore.isValid) {
          console.error('Utilisateur non connecté');
          navigate('/signin');
          return;
        }

        // Utiliser getCurrentCompanyInfo pour récupérer les données de l'entreprise
        const { company: companyData } = await getCurrentCompanyInfo();
        
        // Debug: Afficher les données récupérées
        console.log('Données entreprise récupérées:', companyData);

        // Mettre à jour les états
        if (companyData) {
          setCompany(companyData);
          setInitialCompany(companyData);
          setSelectedCompanyType(companyData.type_of_company || '');
          setSelectedSector(companyData.sector || '');
        } else {
          console.log('Aucune entreprise trouvée, initialisation d\'une nouvelle entreprise');
          setCompany({});
          setInitialCompany({});
        }
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors de la récupération des données:', error);
        setLoading(false);
        showError('Erreur lors de la récupération des données');
      }
    };

    fetchCompanyData();
  }, [navigate]);


  // Gestionnaire de changement pour les conditions
  const handleChange = (id) => {
    if (id === 0) {
      setAcceptConditions(!acceptConditions);
    } else if (id === 1) {
      setAcceptUseConditions(!acceptUseConditions);
    } else if (id === 2) {
      setAcceptRules(!acceptRules);
    }
  };

  // Gestionnaire de changement pour les champs de texte
  const handleCompanyChange = (e) => {
    const { name, value } = e.target;
    console.log(`Champ modifié: ${name} = ${value}`);
    setCompany(prev => ({
      ...prev,
      [name]: value
    }));
  
  };
  
  // Gestionnaire de changement pour les dropdowns
  const handleDropdownChange = (name, value) => {
    if (name === 'type_of_company') {
      setSelectedCompanyType(value);
      setCompany(prev => ({
        ...prev,
        type_of_company: value
      }));
    } else if (name === 'sector') {
      setSelectedSector(value);
      setCompany(prev => ({
        ...prev,
        sector: value
      }));
    }
  };

  // Gestionnaire de changement pour les switches
  const handleSwitchChange = (name, checked) => {
    setCompany(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  // Sauvegarder les modifications
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      // Préparer les données pour la mise à jour ou la création
      const companyData = {
        name: company.name,
        corporate_name: company.corporate_name,
        siret: company.siret,
        address: company.address,
        zip_code: company.zip_code,
        city: company.city,
        country: company.country,
        email: company.email,
        phone: company.phone,
        sector: selectedSector,
        type_of_company: selectedCompanyType,
        employee_count: company.employee_count || 0,
        user_ref: pb.authStore.model.id,
        accept_conditions: acceptConditions,
        accept_rules: acceptRules,
        address_complement: company.address_complement,
        department: company.department,
        director_first_name: company.director_first_name,
        director_last_name: company.director_last_name,
        director_email: company.director_email,
        director_phone: company.director_phone,
        website: company.website || '',
        linkedin: company.linkedin || '',
        facebook: company.facebook || '',
        naf_code: company.naf_code,
        idcc_code: company.idcc_code
      };

      let result;

      if (company.id) {
        // Si l'entreprise existe déjà, mettre à jour ses informations
        result = await updateCompany(company.id, companyData);
        showSuccess('Informations mises à jour avec succès');
      } else {
        // Si l'entreprise n'existe pas encore, la créer
        result = await pb.collection('companies').create(companyData);
        showSuccess('Profil entreprise créé avec succès');
      }

      // Mettre à jour l'état first_connexion et les différentes conditions de l'utilisateur
      // ainsi que la référence à l'entreprise dans le modèle utilisateur
      try {
        await pb.collection('users').update(pb.authStore.model.id, {
          first_connexion: false,
          reglementation: true,
          conditionGeneral: true,
          conditionUtilisation: true,
          company_id: result.id // Ajouter la référence à l'entreprise dans l'utilisateur
        });
        
        // Mettre à jour le modèle d'authentification local pour avoir la référence immédiatement
        const authData = pb.authStore.model;
        pb.authStore.save(pb.authStore.token, {
          ...authData,
          company_id: result.id
        });
        
        console.log('Utilisateur mis à jour avec company_id:', result.id);
      } catch (userUpdateError) {
        console.error('Erreur lors de la mise à jour des informations utilisateur:', userUpdateError);
      }

      // Mettre à jour l'état local avec les données mises à jour
      setCompany(result);
      setInitialCompany(result);

      // Rediriger vers la page d'ajout de tuteur après la sauvegarde réussie
      navigate('/account/add-tutor');

    } catch (error) {
      console.error('Erreur lors de la sauvegarde des informations de l\'entreprise:', error);
      showError('Erreur lors de la sauvegarde: ' + (error.message || 'Veuillez vérifier vos informations'));
    } finally {
      setIsSaving(false);
    }
  };

  return (

    <div className={styles.row}>
      <div
        className={styles.col}
        style={{
          backgroundImage: `url('/images/content/loginbg.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: heightWindow,
          flex: 1
        }}
      />
      <div className={styles.col} style={{ minHeight: heightWindow, flex: 1.5, overflow: 'auto', maxHeight: '100vh' }}>
        <div className={styles.head}>
          <div className={styles.logo}>
            <Image
              className={styles.pic}
              src="/images/logo.png"
              srcDark="/images/logo.png"
              alt="Core"
            />
          </div>
          <div className={cn("h2", styles.title)}>Compléter votre compte</div>
        </div>



        <div className={styles.container}>
          {loading ? (
            <div className={styles.loading}>Chargement des données...</div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className={styles.body}>
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  Adresse
                </h3>
                <div className={styles.formGrid}>
                  <TextInput
                    className={styles.field}
                    label="Nom de l'entreprise"
                    name="name"
                    type="text"
                    placeholder="Nom de l'entreprise"
                    value={company?.name || ''}
                    onChange={handleCompanyChange}
                    required
                  />
                   <TextInput
                    className={styles.field}
                    label="Dénomination sociale"
                    name="corporate_name"
                    type="text"
                    placeholder="Dénomination sociale"
                    value={company?.corporate_name || ''}
                    onChange={handleCompanyChange}
                    required
                  />
                  <div className={styles.field}>
                    <Dropdown
                      className={styles.dropdown}
                      label="Secteur d'activité"
                      name="sector"
                      options={SECTOR_OPTIONS}
                      setValue={(option) => handleDropdownChange('sector', option.value)}
                      value={SECTOR_OPTIONS.find(opt => opt.value === selectedSector) || ''}
                      required
                    />
                  </div>
                  <div className={styles.field}>
                    <Dropdown
                      className={styles.dropdown}
                      label="Type d'entreprise"
                      name="type_of_company"
                      options={COMPANY_TYPE_OPTIONS}
                      setValue={(option) => handleDropdownChange('type_of_company', option.value)}
                      value={COMPANY_TYPE_OPTIONS.find(opt => opt.value === selectedCompanyType) || ''}
                      required
                    />
                  </div>
                  <TextInput
                    className={styles.field}
                    label="Nombre d'employés"
                    name="employee_count"
                    type="number"
                    placeholder="Nombre d'employés"
                    value={company?.employee_count || ''}
                    onChange={handleCompanyChange}
                    required
                  />
                  <TextInput
                    className={styles.field}
                    label="Siret"
                    name="siret"
                    type="text"
                    placeholder="Siret"
                    value={company?.siret || ''}
                    onChange={handleCompanyChange}
                    required
                  />
                  <TextInput
                    className={styles.field}
                    label="Code NAF"
                    name="naf_code"
                    type="text"
                    placeholder="Code NAF"
                    value={company?.naf_code || ''}
                    onChange={handleCompanyChange}
                    required
                  />
                  <TextInput
                    className={styles.field}
                    label="Code IDCC"
                    name="idcc_code"
                    type="text"
                    placeholder="Code IDCC"
                    value={company?.idcc_code || ''}
                    onChange={handleCompanyChange}
                    required
                  />
                  <div className={styles.field} style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginLeft: '10px' }}>Régime spécifique :</span>
                    <Switch
                      name="specific_diet"
                      value={company?.specific_diet || false}
                      onChange={(value) => handleSwitchChange('specific_diet', value)}
                      className={styles.switch}
                    />
                  </div>
                </div>
              </div>


              {/* Adresse de l'entreprise */}
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  Adresse
                </h3>
                <div className={styles.formGrid}>
                  <TextInput
                    className={styles.field}
                    label="Adresse"
                    name="address"
                    type="text"
                    placeholder="Adresse complète"
                    value={company?.address || ''}
                    onChange={handleCompanyChange}
                    required
                  />
                  <TextInput
                    className={styles.field}
                    label="Complément d'adresse"
                    name="address_complement"
                    type="text"
                    placeholder="Ex: Appartement, étage..."
                    value={company?.address_complement || ''}
                    onChange={handleCompanyChange}
                  />
                  <TextInput
                    className={styles.field}
                    label="Code Postal"
                    name="zip_code"
                    type="text"
                    placeholder="Code postal"
                    value={company?.zip_code || ''}
                    onChange={handleCompanyChange}
                    required
                  />
                  <TextInput
                    className={styles.field}
                    label="Ville"
                    name="city"
                    type="text"
                    placeholder="Ville"
                    value={company?.city || ''}
                    onChange={handleCompanyChange}
                    required
                  />
                  <TextInput
                    className={styles.field}
                    label="Département"
                    name="department"
                    type="text"
                    value={company?.department || ''}
                    onChange={handleCompanyChange}
                    required
                  />
                  <TextInput
                    className={styles.field}
                    label="Pays"
                    name="country"
                    type="text"
                    placeholder="Pays"
                    value={company?.country || 'France'}
                    onChange={handleCompanyChange}
                    required
                  />
                </div>
              </div>

              {/* Informations du directeur */}
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  Informations du directeur
                </h3>
                <div className={styles.formGrid}>
                  <TextInput
                    className={styles.field}
                    label="Nom du directeur"
                    name="director_last_name"
                    type="text"
                    placeholder="Nom du directeur"
                    value={company?.director_last_name || ''}
                    onChange={handleCompanyChange}
                    required
                  />
                  <TextInput
                    className={styles.field}
                    label="Prénom du directeur"
                    name="director_first_name"
                    type="text"
                    placeholder="Prénom du directeur"
                    value={company?.director_first_name || ''}
                    onChange={handleCompanyChange}
                    required
                  />
                  <PhoneInput
                    className={styles.field}
                    label="Numéro de téléphone du directeur"
                    name="director_phone"
                    id="director_phone"
                    placeholder="+33 6 XX XX XX XX"
                    value={company?.director_phone || ''}
                    onChange={handleCompanyChange}
                    autoComplete="tel"
                    required
                  />
                  <TextInput
                    className={styles.field}
                    label="Email du directeur"
                    name="director_email"
                    type="email"
                    placeholder="email@example.com"
                    value={company?.director_email || ''}
                    onChange={handleCompanyChange}
                    required
                  />
                </div>
              </div>

              <div className={styles.section} style={{ marginTop: '20px' }}>
                <h3 className={styles.sectionTitle}>
                  Règlement et conditions générales
                </h3>

                {settings.map((x, index) => (
                  <div className={styles.line} key={index}>
                    <div
                      className={styles.title}
                      onClick={() => {
                        if (x.id === 0) {
                          setVisibleCondition(true);
                        } else if (x.id === 1) {
                          setVisibleUseCondition(true);
                        } else if (x.id === 2) {
                          setVisibleReglement(true);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {x.title}
                    </div>
                    <Switch
                      className={styles.switch}
                      value={
                        x.id === 0 ? acceptConditions : 
                        x.id === 1 ? acceptUseConditions : 
                        acceptRules
                      }
                      onChange={() => handleChange(x.id)}
                    />
                  </div>
                ))}

                {/* Modales pour les conditions et le règlement */}
                <ModalCondition
                  visible={visibleCondition}
                  onClose={() => setVisibleCondition(false)}
                  title="Conditions générales"
                  figcaption="Veuillez lire attentivement les conditions générales avant d'accepter."
                />

                <ModalUseCondition
                  visible={visibleUseCondition}
                  onClose={() => setVisibleUseCondition(false)}
                  title="Conditions d'utilisation"
                  figcaption="Veuillez lire attentivement les conditions d'utilisation avant d'accepter."
                />

                <ModalReglement
                  visible={visibleReglement}
                  onClose={() => setVisibleReglement(false)}
                  title="Règlement intérieur"
                  figcaption="Veuillez lire attentivement le règlement intérieur avant d'accepter."
                />

              </div>


              {acceptConditions && acceptUseConditions && acceptRules ? (
                <div className={styles.buttonContainer}>
                  <button
                    className={cn("button", styles.button)}
                    type="submit"
                    disabled={isSaving}
                  >
                    {isSaving ? "Sauvegarde en cours..." : "Suivant"}
                  </button>
                </div>
              ) : (
                <div className={styles.buttonContainer} style={{ opacity: 0.7, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexDirection: isMobile ? 'column' : 'row' }}>
                  <div className={styles.infoText}>
                    Veuillez accepter les conditions générales, les conditions d'utilisation et le règlement intérieur pour continuer
                  </div>
                  <button
                    className={cn("button", styles.button)}
                    type="button"
                    disabled={true}
                    style={{ cursor: 'not-allowed', marginLeft: '15px' }}
                  >
                    Suivant
                  </button>
                </div>
              )}
              </div>
            </form>
          )}
        </div>
      </div>
    </div >
  );
};


export default CompleteAccountCompany;
